import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Grid, Card, CardContent, Chip, 
  Button, CircularProgress, Alert, Divider,
  Checkbox, FormControlLabel, TextField, Switch, Stack
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import { apiClient } from '../../utils/auth';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function ArbitratorAvailabilityForm() {
  const [availability, setAvailability] = useState({
    working_days: [],
    start_time: '',
    end_time: '',
    is_manually_offline: false,
    computed_status: 'OFFLINE'
  });
  const [loading, setLoading] = useState(true);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    fetchAvailability();
  }, []);

  const fetchAvailability = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await apiClient.get('/api/v1/kyc/arbitrator/availability/');
      setAvailability({
        working_days: data.working_days || [],
        start_time: data.start_time || '',
        end_time: data.end_time || '',
        is_manually_offline: !!data.is_manually_offline,
        computed_status: data.computed_status || 'OFFLINE'
      });
    } catch (err) {
      console.warn('Could not fetch availability:', err);
      setError('Failed to load availability settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleDayToggle = (day) => {
    setAvailability(prev => {
      const updated = prev.working_days.includes(day)
        ? prev.working_days.filter(d => d !== day)
        : [...prev.working_days, day];
      return { ...prev, working_days: updated };
    });
  };

  const saveAvailability = async () => {
    setError('');
    setSuccessMsg('');
    setSavingSchedule(true);
    try {
      const { data } = await apiClient.post('/api/v1/kyc/arbitrator/availability/', {
        working_days: availability.working_days,
        start_time: availability.start_time,
        end_time: availability.end_time
      });
      setAvailability(prev => ({
        ...prev,
        working_days: data.working_days || [],
        start_time: data.start_time || '',
        end_time: data.end_time || '',
        computed_status: data.computed_status || 'OFFLINE'
      }));
      setSuccessMsg('Availability schedule updated successfully!');
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.error?.message || 'Failed to update availability schedule.';
      setError(msg);
    } finally {
      setSavingSchedule(false);
    }
  };

  const toggleStatusOverride = async () => {
    setError('');
    setTogglingStatus(true);
    try {
      const { data } = await apiClient.post('/api/v1/kyc/arbitrator/availability/toggle/');
      setAvailability(prev => ({
        ...prev,
        is_manually_offline: !!data.is_manually_offline,
        computed_status: data.computed_status || 'OFFLINE'
      }));
    } catch (err) {
      console.error(err);
      setError('Failed to toggle status mode.');
    } finally {
      setTogglingStatus(false);
    }
  };

  const getComputedStatusChipColor = (status) => {
    switch(status) {
      case 'ONLINE': return 'success';
      case 'BUSY': return 'warning';
      default: return 'error';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#818cf8' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(239,68,68,0.1)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.2)' }}>
          {error}
        </Alert>
      )}

      {successMsg && (
        <Alert severity="success" sx={{ mb: 3, bgcolor: 'rgba(16,185,129,0.1)', color: '#a7f3d0', border: '1px solid rgba(16,185,129,0.2)' }}>
          {successMsg}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Status Mode Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'rgba(13,21,38,0.8)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                  System Status
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, p: 2, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>Current Computed Status</Typography>
                    <Typography variant="h5" fontWeight={800} sx={{ color: availability.computed_status === 'ONLINE' ? '#10b981' : (availability.computed_status === 'BUSY' ? '#f59e0b' : '#ef4444') }}>
                      {availability.computed_status}
                    </Typography>
                  </Box>
                  <Chip 
                    label={availability.computed_status} 
                    color={getComputedStatusChipColor(availability.computed_status)} 
                    size="medium"
                    sx={{ fontWeight: 700, px: 1 }}
                  />
                </Box>
              </Box>

              <Box>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', my: 2 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body1" fontWeight={600}>Manual Offline Override</Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>Temporarily hide from case dispatches</Typography>
                  </Box>
                  <Switch 
                    checked={availability.is_manually_offline}
                    onChange={toggleStatusOverride}
                    disabled={togglingStatus}
                    color="error"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Availability Schedule Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'rgba(13,21,38,0.8)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <WorkHistoryIcon sx={{ color: '#818cf8' }} />
                <Typography variant="h6" fontWeight={700}>Working Schedule</Typography>
              </Box>

              <Typography variant="subtitle2" sx={{ color: '#94a3b8', mb: 1, fontWeight: 600 }}>
                Working Days
              </Typography>

              <Grid container spacing={0.5} sx={{ mb: 2 }}>
                {DAYS_OF_WEEK.map((day) => {
                  const checked = availability.working_days.includes(day);
                  return (
                    <Grid item xs={6} key={day}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={checked}
                            onChange={() => handleDayToggle(day)}
                            sx={{ 
                              color: 'rgba(255,255,255,0.2)',
                              '&.Mui-checked': { color: '#818cf8' }
                            }}
                          />
                        }
                        label={<Typography variant="body2" sx={{ color: checked ? '#f8fafc' : '#94a3b8' }}>{day}</Typography>}
                      />
                    </Grid>
                  );
                })}
              </Grid>

              <Typography variant="subtitle2" sx={{ color: '#94a3b8', mb: 1.5, fontWeight: 600 }}>
                Working Hours
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <TextField
                    label="Start Time"
                    type="time"
                    value={availability.start_time}
                    onChange={(e) => setAvailability(prev => ({ ...prev, start_time: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    fullWidth
                    size="small"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        color: '#f8fafc',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                        '&.Mui-focused fieldset': { borderColor: '#818cf8' },
                      },
                      '& .MuiInputLabel-root': { color: '#94a3b8' }
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="End Time"
                    type="time"
                    value={availability.end_time}
                    onChange={(e) => setAvailability(prev => ({ ...prev, end_time: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    fullWidth
                    size="small"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        color: '#f8fafc',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                        '&.Mui-focused fieldset': { borderColor: '#818cf8' },
                      },
                      '& .MuiInputLabel-root': { color: '#94a3b8' }
                    }}
                  />
                </Grid>
              </Grid>

              <Button
                fullWidth
                variant="contained"
                onClick={saveAvailability}
                disabled={savingSchedule}
                startIcon={savingSchedule ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <SaveIcon />}
                sx={{
                  bgcolor: '#4f46e5',
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#4338ca' }
                }}
              >
                Save Schedule Changes
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
